import { useEffect, useState } from 'react';
import {
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Divider,
} from '@newulife/common';
import SectionCard from './SectionCard';
import Input from 'components/common/inputs/Input';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { inputState } from 'state';
import { findInDictionary } from 'helpers/parse';

function MYLegalSection(props) {
  const inputs = useRecoilValue(inputState);
  const setInputs = useSetRecoilState(inputState);
  const { config, isValid, data = {}, inputs: dictionary } = props;
  const { title } = data;

  const fields = config?.enrollment?.form?.sections?.my_legal?.fields;

  // Validation states for fields - all optional fields start as valid (empty optional fields are valid)
  const [CompanyRegistrationNumberIsValid, set_CompanyRegistrationNumberIsValid] = useState(true);
  const [IndustriesCodeIsValid, set_IndustriesCodeIsValid] = useState(true);
  const [PassportNumberIsValid, set_PassportNumberIsValid] = useState(true);
  const [IdentityCardNumberIsValid, set_IdentityCardNumberIsValid] = useState(true);
  const [TINIsValid, set_TINIsValid] = useState(true);
  const [companyInfoIsValid, set_CompanyInfoIsValid] = useState(true);

  // Get current value from inputs state
  const MALDistributor = inputs.miscFields?.MALDistributor ?? '';

  // Initialize MALDistributor to true if not set
  useEffect(() => {
    if (
      inputs.miscFields?.MALDistributor === undefined ||
      inputs.miscFields?.MALDistributor === ''
    ) {
      setInputs((prev) => ({
        ...prev,
        miscFields: {
          ...prev.miscFields,
          MALDistributor: true,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle radio group change
  const handleChange = (event) => {
    const value = event.target.value === 'true';

    setInputs((prev) => ({
      ...prev,
      miscFields: {
        ...prev.miscFields,
        MALDistributor: value,
      },
    }));
  };

  // Input props shared across all Input components
  const inputProps = {
    dictionary,
    country: config.market,
    market: config.market,
    data,
    storeInMiscFields: true,
  };

  useEffect(() => {
    set_CompanyInfoIsValid(CompanyRegistrationNumberIsValid && IndustriesCodeIsValid);
  }, [CompanyRegistrationNumberIsValid, IndustriesCodeIsValid]);

  // Mark non-displayed fields as valid (Input component handles validation for displayed fields)
  useEffect(() => {
    // Company fields: mark as valid if not displayed
    if (!inputs?.COMPANY) {
      set_CompanyRegistrationNumberIsValid(true);
      set_IndustriesCodeIsValid(true);
    }
  }, [inputs?.COMPANY]);

  useEffect(() => {
    // TIN: mark as valid if not displayed (company path or non-Malaysian)
    if (inputs?.COMPANY || MALDistributor === false) {
      set_TINIsValid(true);
    }
  }, [inputs?.COMPANY, MALDistributor]);

  useEffect(() => {
    // PassportNumber: mark as valid if not displayed (Malaysian path)
    if (MALDistributor !== false) {
      set_PassportNumberIsValid(true);
    }
  }, [MALDistributor]);

  useEffect(() => {
    // IdentityCardNumber: mark as valid if not displayed (non-Malaysian path)
    if (MALDistributor === false) {
      set_IdentityCardNumberIsValid(true);
    }
  }, [MALDistributor]);

  useEffect(() => {
    // If no fields, section is valid
    if (!fields?.length) {
      isValid(true);
      return;
    }

    // If company name, then validate thosoe text fields only
    if (inputs?.COMPANY) {
      isValid(companyInfoIsValid);
      return;
    }

    // If no company name:
    // - Malaysian: validate TIN, and IdentityCardNumber
    // - Non-Malaysian: validate PassportNumber only (TIN not required)
    const isNonMalaysian = MALDistributor === false;

    const idOrPassportValid = isNonMalaysian ? PassportNumberIsValid : IdentityCardNumberIsValid;
    
    // TIN is only required for Malaysians
    const allFieldsValid = isNonMalaysian
      ? idOrPassportValid
      : TINIsValid && idOrPassportValid;

    isValid(allFieldsValid);

    // LEAVE isValid out of the dependnecy array or it will not move to "review" page - James
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    inputs?.COMPANY,
    companyInfoIsValid,
    MALDistributor,
    TINIsValid,
    PassportNumberIsValid,
    IdentityCardNumberIsValid,
  ]);

  if (!fields?.length) return null;

  return (
    <SectionCard title={title}>
      <FormControl>
        <FormLabel id="MALDistributor-label" sx={{ marginBottom: '10px' }}>
          {findInDictionary(dictionary, 'CITIZENSHIP_MY')}
        </FormLabel>
        <RadioGroup
          aria-labelledby="MALDistributor-label"
          name="MALDistributor-radio-group"
          value={MALDistributor === false ? 'false' : 'true'}
          onChange={handleChange}
        >
          <FormControlLabel
            value="true"
            control={<Radio />}
            label={findInDictionary(dictionary, 'MALAYSIAN_MY') || ''}
          />
          <FormControlLabel
            value="false"
            control={<Radio />}
            label={findInDictionary(dictionary, 'NON_MALAYSIAN_MY') || ''}
          />
        </RadioGroup>
      </FormControl>
      {MALDistributor !== false && (
        <Input id="TIN" {...config.inputs['TIN']} {...inputProps} isValid={set_TINIsValid} />
      )}
      {MALDistributor === false ? (
        <Input
          id="PassportNumber"
          {...config.inputs['PassportNumber']}
          {...inputProps}
          isValid={set_PassportNumberIsValid}
          required={MALDistributor === false}
        />
      ) : (
        <Input
          id="IdentityCardNumber"
          {...config.inputs['IdentityCardNumber']}
          {...inputProps}
          isValid={set_IdentityCardNumberIsValid}
          required={MALDistributor !== false}
        />
      )}
      {inputs?.COMPANY && (
        <>
          <Divider />
          <Input
            id="CompanyRegistrationNumber"
            {...config.inputs['CompanyRegistrationNumber']}
            {...inputProps}
            isValid={set_CompanyRegistrationNumberIsValid}
            required={inputs?.COMPANY}
          />
          <Input
            id="IndustriesCode"
            {...config.inputs['IndustriesCode']}
            {...inputProps}
            isValid={set_IndustriesCodeIsValid}
            required={inputs?.COMPANY}
          />
        </>
      )}
    </SectionCard>
  );
}
export default MYLegalSection;
